import * as z from 'zod';

const MAX_AMOUNT = 100_000_000_000;
const CURRENCY_MASK_CHARS = /[$,\s]/g;

// Currency fields are stored as the masked string from InputCurrency (e.g.
// `$1,234,567`). These helpers wrap a numeric validator with a pre-transform
// that strips the mask characters and coerces to a number. Anything the user
// can produce by typing through the mask is accepted; non-numeric junk fails
// before the numeric checks even run.
const baseNumber = z
  .number()
  .min(0, {error: 'Amount cannot be negative'})
  .max(MAX_AMOUNT, {
    error: `Amount cannot exceed $${MAX_AMOUNT.toLocaleString()}`,
  });

// Required: empty/missing string fails the "Please enter an amount" check.
export const requiredCurrencyAmount = z
  .string({error: 'Please enter an amount'})
  .trim()
  .min(1, {error: 'Please enter an amount'})
  .transform((s, ctx) => {
    const parsed = Number(s.replace(CURRENCY_MASK_CHARS, ''));

    if (Number.isNaN(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid amount',
      });

      return z.NEVER;
    }

    return parsed;
  })
  .pipe(baseNumber);

// Optional: missing or empty string passes (returns undefined). When present,
// must parse to a valid non-negative number under the ceiling.
export const optionalCurrencyAmount = z
  .string()
  .optional()
  .transform((s, ctx) => {
    if (s === undefined || s.trim() === '') return undefined;

    const parsed = Number(s.replace(CURRENCY_MASK_CHARS, ''));

    if (Number.isNaN(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid amount',
      });

      return z.NEVER;
    }

    return parsed;
  })
  .pipe(baseNumber.optional());
